import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MedicalQuestionnaireProgressDonut from './MedicalQuestionnaireProgressDonut';
import { fetchMyProfile } from '../services/profile';
import { setProfile } from '../store/userSlice';
import { isDoctorNurseRole } from '../utils/signInRedirect';

function getEffectiveRole(role, viewAsRole) {
  if (role === 'Supervisor') {
    return viewAsRole === 'Doctor/Nurse' ? 'Doctor/Nurse' : 'Patient';
  }
  return role;
}

function formatIncompleteSectionsLine(count) {
  const n = Math.max(0, Number(count) || 0);
  if (n === 0) return 'Alle secties voltooid';
  if (n === 1) return '1 sectie incompleet';
  return `${n} secties incompleet`;
}

function formatIncompleteBadge(count) {
  const n = Math.max(0, Number(count) || 0);
  if (n === 0) return 'Voltooid';
  if (n === 1) return '1 incompleet';
  return `${n} incompleet`;
}

function isQuestionnaireOverallComplete(status) {
  return String(status || '').trim().toLowerCase() === 'complete';
}

function hasQuestionnaireSummaryData(user) {
  if (!user) return false;
  if (Number.isFinite(user.totalSections) && user.totalSections > 0) return true;
  if (Number.isFinite(user.completedSectionsPercentage)) return true;
  if (user.overallStatus) return true;
  return false;
}

export default function MedicalQuestionnaireSummaryCard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user);
  const userRole = useSelector((s) => s.user.role);
  const viewAsRole = useSelector((s) => s.user.viewAsRole);
  const effectiveRole = getEffectiveRole(userRole, viewAsRole);
  const [isLoading, setIsLoading] = useState(false);

  const {
    totalSections,
    totalCompletedSections: completedSections,
    totalIncompleteSections: incompleteSections,
    completedSectionsPercentage: completedPercent,
    section_progress: sectionProgress,
    sections_completed: sectionsCompleted,
    overallStatus,
  } = user;

  useEffect(() => {
    if (isDoctorNurseRole(effectiveRole)) {
      return undefined;
    }

    if (
      hasQuestionnaireSummaryData({
        totalSections,
        completedSectionsPercentage: completedPercent,
        overallStatus,
      })
    ) {
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const profile = await fetchMyProfile();
        if (cancelled) return;
        dispatch(
          setProfile({
            totalSections: profile.totalSections,
            totalCompletedSections: profile.totalCompletedSections,
            totalIncompleteSections: profile.totalIncompleteSections,
            completedSectionsPercentage: profile.completedSectionsPercentage,
            section_progress: profile.section_progress,
            sections_completed: profile.sections_completed,
            overallStatus: profile.overallStatus,
          })
        );
      } catch {
        // ProfileCard may still hydrate; keep card hidden if no data
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch, totalSections, completedPercent, overallStatus, effectiveRole]);

  if (isDoctorNurseRole(effectiveRole)) {
    return null;
  }

  if (!hasQuestionnaireSummaryData(user)) {
    if (isLoading) {
      return (
        <div className="mb-6 w-full max-w-[1040px] rounded-2xl border border-[#E6E6E6] bg-white px-4 py-4 shadow-sm md:mb-8 md:px-6 md:py-5">
          <p className="py-4 text-center font-poppins text-sm text-[#777777]">
            Laden van vragenlijstsamenvatting…
          </p>
        </div>
      );
    }
    return null;
  }

  const safeTotal = Number.isFinite(totalSections) && totalSections > 0 ? totalSections : null;
  const safeCompleted = Number.isFinite(sectionsCompleted)
    ? sectionsCompleted
    : Number.isFinite(completedSections)
      ? completedSections
      : 0;
  const safeIncomplete = Number.isFinite(incompleteSections) ? incompleteSections : 0;
  const displayPercent = Number.isFinite(sectionProgress)
    ? Math.round(sectionProgress)
    : Number.isFinite(completedPercent)
      ? Math.round(completedPercent)
      : 0;
  const isComplete = isQuestionnaireOverallComplete(overallStatus);

  return (
    <div className="mb-6 w-full max-w-[1040px] rounded-2xl border border-[#E6E6E6] bg-white px-4 py-4 shadow-sm md:mb-8 md:px-6 md:py-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-4 md:gap-5">
          <MedicalQuestionnaireProgressDonut
            percent={displayPercent}
            size={72}
            strokeWidth={5}
          />
          <div className="min-w-0 font-poppins">
            <h2 className="text-base font-semibold text-[#1A1A1A] md:text-lg">
              Bekijk vragenlijstsamenvatting
            </h2>
            <p className="mt-1 text-sm text-[#777777]">
              {safeTotal != null ? (
                <>
                  {safeCompleted} van {safeTotal} secties ·{' '}
                  {formatIncompleteSectionsLine(safeIncomplete)}
                </>
              ) : (
                formatIncompleteSectionsLine(safeIncomplete)
              )}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-3 sm:gap-4 lg:justify-end">
          {isComplete ? (
            <span className="rounded-full bg-[#DDF0EA] px-3 py-1 text-xs font-medium text-[#119975] md:text-sm">
              {overallStatus || 'Voltooid'}
            </span>
          ) : safeIncomplete > 0 ? (
            <span className="rounded-full bg-[#FAEEDA] px-3 py-1 text-xs font-medium text-[#854F0B] md:text-sm">
              {formatIncompleteBadge(safeIncomplete)}
            </span>
          ) : overallStatus ? (
            <span className="rounded-full bg-[#FAEEDA] px-3 py-1 text-xs font-medium text-[#854F0B] md:text-sm">
              {overallStatus}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => navigate('/medical-questionnaire-summary?self=true')}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#119975] px-6 font-poppins text-sm font-medium text-white transition-opacity hover:opacity-95 md:h-12 md:px-8 md:text-base"
          >
            Bekijk samenvatting
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}
